import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../context/PageTitleContext';

// The page an invite email links to (#358). A friend who opens it may be
// signed in, have an account but be signed out, or be new. Whichever account
// they sign in with is the one that connects: the email was only how the
// invite reached them. iOS has no universal links yet (#8), so phones land here too.
function InvitePage() {
  const { token: inviteToken } = useParams();
  const { token, isAuthenticated } = useAuth();
  const { setPageTitle } = usePageTitle();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    setPageTitle('Invitation');
  }, [setPageTitle]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(
          `${API_BASE_URL}/users/social/invites/token/${encodeURIComponent(inviteToken)}`
        );
        if (!response.ok) throw new Error('Could not load this invite');
        const data = await response.json();
        if (!cancelled) setInvite(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [API_BASE_URL, inviteToken]);

  const handleAccept = async () => {
    setAccepting(true);
    setError('');
    try {
      const response = await fetch(
        `${API_BASE_URL}/users/social/invites/token/${encodeURIComponent(inviteToken)}/accept`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        // Own invite, blocked, or no longer active: the server's message is written for people.
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Could not accept this invite');
      }
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setAccepting(false);
    }
  };

  const inviterName = invite?.inviter?.display_name || 'A dyner.z member';
  const back = encodeURIComponent(`/invite/${inviteToken}`);

  let body;
  if (loading) {
    body = (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" />
        <p className="mt-2">Loading invite...</p>
      </div>
    );
  } else if (result) {
    const friend = result.friend;
    body = (
      <>
        <Alert variant="success">
          {result.status === 'already_friends'
            ? `You and ${friend.display_name || inviterName} are already friends.`
            : `You and ${friend.display_name || inviterName} are now friends.`}
        </Alert>
        <Button as={Link} to={`/profile/${friend.user_id}`} variant="primary">
          View {friend.display_name || 'their'} profile
        </Button>
      </>
    );
  } else if (!invite || !invite.active) {
    body = (
      <>
        {error && <Alert variant="danger">{error}</Alert>}
        <p className="text-muted">{invite?.message || 'This invite is no longer active'}.</p>
        <Button as={Link} to="/" variant="outline-primary">Explore dyner.z</Button>
      </>
    );
  } else {
    body = (
      <>
        <h4 className="mb-3">{inviterName} invited you to connect</h4>
        <p className="text-muted">
          Friends on dyner.z follow each other.
        </p>
        {error && <Alert variant="danger">{error}</Alert>}
        {isAuthenticated ? (
          <Button variant="primary" onClick={handleAccept} disabled={accepting}>
            {accepting ? 'Connecting...' : `Connect with ${inviterName}`}
          </Button>
        ) : (
          <>
            <p>Already on dyner.z? Sign in with whichever account you use. New here? Join first.</p>
            <div className="d-flex gap-2">
              <Button as={Link} to={`/login?next=${back}`} variant="primary">Sign in</Button>
              <Button as={Link} to={`/login?tab=register&next=${back}`} variant="outline-primary">
                Join dyner.z
              </Button>
            </div>
          </>
        )}
      </>
    );
  }

  return (
    <Container className="mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <Card>
            <Card.Body className="p-4">{body}</Card.Body>
          </Card>
        </div>
      </div>
    </Container>
  );
}

export default InvitePage;
